import { message as antdMessage } from "antd";
import type { ReactNode } from "react";

/**
 * Drop-in replacement for antd's static `message`.
 *
 * antd v6 removed the `ant-message-custom-content` class from the success
 * toast (it is now `.ant-message-notice-success`), which broke UI-automation
 * selectors that matched the old class. Rather than migrate 100+
 * `message.success(...)` call sites individually, callers simply import
 * `message` from here instead of from "antd"; every other method
 * (`error`, `warning`, `info`, `loading`, `open`, `config`, `useMessage`, …)
 * is re-exported unchanged, and only `success` is wrapped so its content
 * carries a stable, class-independent `data-testid`.
 */
export const SUCCESS_TOAST_TESTID = "success-toast-message";

const wrapContent = (node: ReactNode) => (
  <span data-testid={SUCCESS_TOAST_TESTID}>{node}</span>
);

// antd's success accepts either (content, duration?, onClose?) or a single
// config object ({ content, duration, onClose, ... }). Handle both, wrapping
// the rendered content in either case.
const success = (content: any, ...rest: any[]) => {
  // Unit tests assert the exact value handed to `message.success`, and the
  // automation hook is only needed in the running app (dev/prod) that
  // Playwright drives — so under vitest we pass the content through unchanged.
  // The toast still shows the same text either way; the wrapper only adds a
  // transparent, class-independent `data-testid` span in the real app.
  if (process.env.NODE_ENV === "test") {
    return antdMessage.success(content, ...rest);
  }

  const isConfigObject =
    content !== null && typeof content === "object" && !("$$typeof" in content);

  if (isConfigObject) {
    return antdMessage.success({
      ...content,
      content: wrapContent(content.content)
    });
  }

  return antdMessage.success(wrapContent(content), ...rest);
};

export const message = { ...antdMessage, success };

export default message;
